/**
 * "Now pitching: <name>" floating banner. Mounted as a sibling of the
 * FieldDiamond. Parent (GameView) tracks prev-pitcher-id refs and triggers
 * a transient state when an id changes; the banner mounts for ~6s and
 * fades away. The banner positions absolutely within the field column so
 * it sits over the green area without disturbing layout.
 */
import { useEffect, useState } from 'react';
import styles from './PitchingChangeBanner.module.css';

interface PitchingChangeBannerProps {
  pitcherName: string | null;
  jersey: string | null;
  /** ID of the new pitcher — bumping this restarts the visible timer. */
  triggerKey: number;
}

const VISIBLE_MS = 6000;

export default function PitchingChangeBanner({
  pitcherName,
  jersey,
  triggerKey,
}: PitchingChangeBannerProps): JSX.Element | null {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), VISIBLE_MS);
    return () => window.clearTimeout(t);
  }, [triggerKey]);

  if (!pitcherName || !visible) return null;
  return (
    <div className={styles.banner} data-testid="pitching-change-banner" role="status">
      <span className={styles.label}>Now pitching</span>
      <span className={styles.name}>{pitcherName}</span>
      {jersey && <span className={styles.jersey}>#{jersey}</span>}
    </div>
  );
}
